import { create } from 'zustand'
import type { ApiClient } from '../types'

interface Organization {
  organizationId: string
  name: string
  [key: string]: unknown
}

interface OrganizationStore {
  organization: Organization | null
  isLoading: boolean
  error: string | null
  lastFetched: number | null
  fetchOrganization: (api: ApiClient) => Promise<Organization | null>
  updateOrganization: (api: ApiClient, updates: Partial<Organization>) => Promise<Organization | null>
  clearCache: () => void
}

/**
 * 組織設定ストア
 * 組織ごとの設定を管理
 *
 * 注: 通話種別オプション(callTypeOptions)とよく使うタグ(quickTags)は
 * 電話番号ごとに設定されるため、このストアでは管理しません。
 * 各電話番号の設定は /api/phone-numbers から取得してください。
 */
export const useOrganizationStore = create<OrganizationStore>((set, get) => ({
  // 組織設定
  organization: null,
  isLoading: false,
  error: null,
  lastFetched: null,

  // 組織設定を取得
  fetchOrganization: async (api) => {
    const { lastFetched } = get()

    // 5分以内に取得済みならキャッシュを使用
    if (lastFetched && Date.now() - lastFetched < 5 * 60 * 1000) {
      return get().organization
    }

    set({ isLoading: true, error: null })

    try {
      const data = await api.get<Organization>('/api/organization')
      set({
        organization: data,
        isLoading: false,
        lastFetched: Date.now(),
      })
      return data
    } catch (error) {
      console.error('組織設定取得エラー:', error)
      set({
        error: (error as Error).message,
        isLoading: false,
      })
      return null
    }
  },

  // 組織設定を更新（管理者用）
  updateOrganization: async (api, updates) => {
    set({ isLoading: true, error: null })

    try {
      const data = await api.put<Organization>('/api/organization', updates)
      set({
        organization: data,
        isLoading: false,
        lastFetched: Date.now(),
      })
      return data
    } catch (error) {
      console.error('組織設定更新エラー:', error)
      set({
        error: (error as Error).message,
        isLoading: false,
      })
      throw error
    }
  },

  // キャッシュをクリア
  clearCache: () => {
    set({
      organization: null,
      lastFetched: null,
    })
  },
}))
